import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import S from './AddQuickNoteStyles';
import Input from '../../common/Input/Input';
import usePostNote from '../../../API/hooks/notes/usePostNote';
import { PostNoteRequest } from '../../../API/types/Notes';
import { NoteTitles } from '../../../API/services/NotesService';

type Props = {
  entityName: string; // 'alerts' | 'drivers' | 'vehicles' | 'devices' | 'trips' | 'videos'
  entityId: string;
  fleetId: string;
};

function AddQuickNote({ entityName, entityId, fleetId }: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => {};
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePostNote(onSubmitSuccess, onSubmitFail);

  const formData = {
    fleetId,
    entityId,
    entityName,
  };

  const formik = useFormik({
    initialValues: {
      text: '',
      title: '',
      // title: NoteTitles[entityName] as string,
    },
    onSubmit: (values) => {
      mutate({ ...values, ...formData } as unknown as PostNoteRequest);
      formik.values.text = '';
      formik.values.title = '';
    },
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.text.length) errors.text = 'Note can\'t be empty';
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const onPressEnter = (e: any): void => {
    if (e.key === 'Enter') {
      formik.submitForm();
    }
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <Input
          label="Add a note here:"
          name="text"
          placeholder="Describe your point here"
          onChange={handleInputChange}
          onBlur={() => { formik.setTouched({}); }}
          onKeyDown={onPressEnter}
          value={formik.values.text}
          error={formik.touched.text && formik.errors.text}
        />
        <Input
          label="Title for note:"
          name="title"
          placeholder="Type a title for note here (optional)"
          onChange={handleInputChange}
          onBlur={() => { formik.setTouched({}); }}
          onKeyDown={onPressEnter}
          value={formik.values.title}
          error={formik.touched.title && formik.errors.title}
        />
        {!!submitError && (
          typeof submitError === 'string'
            ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
            : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
        )}
      </form>
    </S.Wrapper>
  );
}

export default AddQuickNote;
